import React, { useEffect, useState } from 'react';
import { Table, Modal, Breadcrumb, Button, Card, Row, Col, Tag, Input, Tabs, Select, Space, message } from 'antd'; import { HomeOutlined, EyeOutlined, CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useUserProfile from '../../useUserProfile';
import { Tooltip, Descriptions } from 'antd'; // Import Tooltip from Ant Design
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import './AdminApprovalComponent.css';

// Tinted square icon button for the row actions (View / Delete), with its tooltip.
const IconAction: React.FC<{
  title: string;
  icon: React.ReactNode;
  variant: 'view' | 'delete';
  onClick: () => void;
  disabled?: boolean;
}> = ({ title, icon, variant, onClick, disabled }) => (
  <Tooltip title={title}>
    <Button
      type="text"
      aria-label={title}
      className={`aa-icon-btn aa-icon-btn--${variant}`}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
    />
  </Tooltip>
);

interface Nomination {
  approvalId: string;
  nominationId: string;
  nominatorName: string;
  nomineeNames: string[];
  rewardName: string;
  rewardCategory: string;
  approvalStatus: string;
  approvalComments: string;
  unit: string;
  adminApproval: string;
  budget: number;
  nominationDate: string;
  // Level-1 approver (unit head) and their GEM budget; counts are null when they have no unit budget.
  unitHeadName?: string | null;
  unitHeadAwardsCount?: number | null;
  unitHeadAwardsUsed?: number | null;
}

interface NominationDetails {
  nominationId: string;
  nominatorName: string;
  nominatorId: string;
  nomineeNames: string[];
  nomineeIds: string[];
  rewardName: string;
  rewardCategory: string;
  budget: number;
  approvalStatus: string;
  approvalComments: string;
  nominationDate: string;
  nominationMonth: string;
  nominationYear: number;
  approverLevel1: string;
  approverLevel2: string;
  valueDemonstrated: string;
  ecardName: string;
  ecardImage: string;
  rewardTestimonial: string;
}

// Pull a human-readable reason out of an axios error. The approvals API returns the reason as a
// plain string body (BadRequest("...")), while model-binding failures come back as an ASP.NET
// ProblemDetails object — handle both, and fall back to the supplied default.
const extractApiError = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title || data?.detail) return [data.title, data.detail].filter(Boolean).join(' — ');
  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors).flat().filter(Boolean) as string[];
    if (messages.length) return messages.join(' ');
  }
  return fallback;
};

const AdminApprovalComponent: React.FC = () => {
  // Format a numeric budget value as USD with a $ sign and comma grouping (e.g. $1,234 or $1,234.56)
  const formatUSD = (value: number) =>
    `$${Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [selectedNomination, setSelectedNomination] = useState<NominationDetails | null>(null);
  const [totalBudget, setTotalBudget] = useState(0);
  const [budgetUsed, setBudgetUsed] = useState(0);
  const [budgetRemaining, setBudgetBalance] = useState(0);
  // Loading flags for the three approval tables (drive the antd Table spinner).
  const [rewardsLoading, setRewardsLoading] = useState(false);
  // Rows per page in the GEM Approvals and Admin Only Approvals tables, each shared by its
  // All/Pending/Approved/Rejected sub-tabs.
  const [gemPageSize, setGemPageSize] = useState(10);
  const [adminPageSize, setAdminPageSize] = useState(10);
  const [adminLoading, setAdminLoading] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const { requestProfileData } = useUserProfile();
  const [adminNominations, setAdminNominations] = useState<Nomination[]>([]);
  const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('1'); // Track active tab
  const [isEditingCitation, setIsEditingCitation] = useState(false);
  const [editedCitation, setEditedCitation] = useState('');
  const [editedContribution, setEditedContribution] = useState('');
  const { instance, accounts } = useMsal();
  const [allNominations, setAllNominations] = useState<Nomination[]>([]);
  const [searchText, setSearchText] = useState('');
  const [rewardsApprovalSearch, setRewardsApprovalSearch] = useState('');
  const [adminOnlyApprovalSearch, setAdminOnlyApprovalSearch] = useState('');
  // Track nominations that are currently being approved/rejected so we can
  // immediately disable the Approve/Reject buttons and prevent duplicate
  // submissions (duplicate mail + duplicate amount pushed to Vantage).
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  // Admin_Only_Approvals bulk selection (checkboxes) and bulk-action progress.
  const [adminSelectedKeys, setAdminSelectedKeys] = useState<React.Key[]>([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Year / Quarter / Month filters for the GEM Approvals tab.
  // Default to the current year and current quarter (Month = All).
  const nowDate = new Date();
  const currentYear = nowDate.getFullYear();
  const currentQuarter = Math.ceil((nowDate.getMonth() + 1) / 3);
  const [selectedYear, setSelectedYear] = useState<string>(String(currentYear));
  const [selectedQuarter, setSelectedQuarter] = useState<string>(String(currentQuarter));
  const [selectedMonth, setSelectedMonth] = useState<string>('All');

  // Year / Award Name filters for the Admin_Only_Approvals tab.
  const [adminSelectedYear, setAdminSelectedYear] = useState<string>(String(currentYear));
  const [adminSelectedAward, setAdminSelectedAward] = useState<string>('All');

  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTHS_BY_QUARTER: Record<string, number[]> = {
    '1': [1, 2, 3], '2': [4, 5, 6], '3': [7, 8, 9], '4': [10, 11, 12],
  };

  // Years available in the data (plus current year), newest first.
  const availableYears = (() => {
    const years = new Set<number>([currentYear]);
    nominations.forEach(n => {
      if (n.nominationDate) {
        const y = new Date(n.nominationDate).getFullYear();
        if (!isNaN(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  })();

  // Months shown in the Month dropdown depend on the selected quarter.
  const monthOptions = selectedQuarter === 'All'
    ? Array.from({ length: 12 }, (_, i) => i + 1)
    : (MONTHS_BY_QUARTER[selectedQuarter] || []);

  const handleQuarterChange = (value: string) => {
    setSelectedQuarter(value);
    // Reset month when quarter changes, since the previously selected month
    // may not belong to the newly selected quarter.
    setSelectedMonth('All');
  };

  // True if the given nomination date matches the current Year/Quarter/Month filter.
  const matchesDateFilter = (dateStr: string): boolean => {
    if (selectedYear === 'All' && selectedQuarter === 'All' && selectedMonth === 'All') {
      return true;
    }
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const year = d.getFullYear();
    const month = d.getMonth() + 1; // 1-12

    if (selectedYear !== 'All' && year !== Number(selectedYear)) return false;
    if (selectedMonth !== 'All') {
      // A specific month takes precedence over the quarter.
      return month === Number(selectedMonth);
    }
    if (selectedQuarter !== 'All') {
      return Math.ceil(month / 3) === Number(selectedQuarter);
    }
    return true;
  };

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === '1') {
      fetchNominations();
      fetchAwardsData();
    } else if (key === '2') {
      fetchAdminNominations(true); // pass true for admin
      fetchAwardsData();
    } else if (key === '3') {
      fetchAllNominations();
      fetchAwardsData();
    }
  };

  useEffect(() => {
    fetchToken();
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        await requestProfileData();
        const userMail = localStorage.getItem("UserMail");
        const userName = localStorage.getItem("UserName");
        const userId = localStorage.getItem("UserId");
        setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [requestProfileData]);
  const fetchAllNominations = async () => {
    setAllLoading(true);
    try {
      const token = await fetchToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/GetApprovalsData/${false}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const nominationsWithIndex = response.data.map((nomination: Nomination, index: number) => ({
        ...nomination,
        index: index + 1,
      }));
      setAllNominations(nominationsWithIndex);
    } catch (error) {
      console.error("Error fetching all nominations", error);
    } finally {
      setAllLoading(false);
    }
  };
  const fetchAdminNominations = async (isAdmin = false) => {
    setAdminLoading(true);
    try {
      const token = await fetchToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/GetApprovalsData/${isAdmin}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const nominationsWithIndex = response.data.map((nomination: Nomination, index: number) => ({
        ...nomination,
        index: index + 1,
      }));
      setAdminNominations(nominationsWithIndex);
    } catch (error) {
      console.error("Error fetching admin nominations", error);
    } finally {
      setAdminLoading(false);
    }
  };
  useEffect(() => {
    if (UserDetails.length > 0 && UserDetails[0]?.id) {
      fetchNominations();
      fetchAwardsData();
      fetchAdminNominations();
    }
  }, [UserDetails]);
  const fetchNominations = async () => {
    setRewardsLoading(true);
    try {
      const token = await fetchToken();
      const Managerid = UserDetails[0]?.id;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/GetApprovalsDataRequiresApproval`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const nominationsWithIndex = response.data.map((nomination: Nomination, index: number) => ({
        ...nomination,
        index: index + 1,
      }));
      setNominations(nominationsWithIndex);
    } catch (error) {
      console.error("Error fetching nominations", error);
    } finally {
      setRewardsLoading(false);
    }
  };
  const handleDeleteNomination = async (nominationId: string) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this nomination? it will be permanently removed.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const token = await fetchToken();
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/${nominationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          fetchNominations();
          fetchAdminNominations();
          fetchAwardsData();
        } catch (error) {
          console.error('Error deleting nomination', error);
        }
      },
    });
  };
  const fetchAwardsData = async () => {
    try {
      const token = await fetchToken();
      const Managerid = UserDetails[0]?.id;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Budget/active-main`, {
        headers: {
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      setTotalBudget(response.data.totalBudget);
      setBudgetUsed(response.data.usedBudget);
      setBudgetBalance(response.data.balance);

    } catch (error) {
      console.error("Error fetching awards data", error);
    }
  };

  useEffect(() => {
    if (UserDetails.length > 0 && UserDetails[0]?.id) {
      fetchNominations();
      fetchAwardsData();
    }
  }, [UserDetails]);

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleViewNomination = async (nominationId: string) => {
    try {
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/nomination/${nominationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.length > 0) {
        setSelectedNomination(response.data[0]);
      }
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error fetching nomination details", error);
    }
  };

  const extractCitation = (description: string): string => {
    const citationRegex = /~citation~([\s\S]*?)~FormFields~|~citation~([\s\S]*)/;
    const match = description.match(citationRegex);
    return match ? (match[1] || match[2] || '').trim() : description;
  };

  const extractBusinessOutcome = (description: string): string => {
    const contributionRegex = /~businessOutcome~([\s\S]*)/;
    const match = description.match(contributionRegex);
    return match ? match[1].trim() : '';
  };
  const handleSubmitNomination = async (nominationId: string, action: 'Approved' | 'Rejected') => {
    const userid = UserDetails[0]?.id;
    if (!userid) return;
    // Guard against double submission: if this nomination is already being
    // processed, ignore the extra click.
    if (processingIds.includes(nominationId)) return;
    // Disable the Approve/Reject buttons for this row immediately, before the
    // async round-trip, so the admin cannot fire the mail / Vantage amount twice.
    setProcessingIds(prev => [...prev, nominationId]);
    try {
      const token = await fetchToken();
      // Map jsonData to the expected format and validate required fields
      const mappedData = [{
        NominationId: nominationId || '',
        Status: action || ''
      }];

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/UploadApprovalsData`,
        {
          UserId: userid,
          NominationLists: mappedData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

      if (activeTab === '1') {
        fetchNominations();
      } else if (activeTab === '2') {
        fetchAdminNominations();
      }

      fetchNominations();
      fetchAwardsData();
      setIsModalVisible(false);
      message.success(action === 'Approved' ? 'Nomination approved.' : 'Nomination rejected.');
    } catch (error) {
      console.error(`Error ${action}ing nomination`, error);
      // Show the reason the API returned. Without this the admin saw nothing at all — the failure
      // only existed as a console error, so every prod report had to be diagnosed from server logs.
      message.error(extractApiError(error, `The nomination could not be ${action === 'Approved' ? 'approved' : 'rejected'}.`), 10);
      // Re-enable the buttons only if the submission failed, so the admin can retry.
      setProcessingIds(prev => prev.filter(id => id !== nominationId));
    }
  };
  // Admin approves a pending GEM nomination at level 1 on behalf of its unit head. The API sets
  // approval1_status to Approved with an explanatory comment and takes one award from the unit
  // head's budget - only if a whole award is left; otherwise it refuses and nothing changes.
  const handleApproveForUnitHead = async (record: Nomination) => {
    if (processingIds.includes(record.nominationId)) return;
    setProcessingIds(prev => [...prev, record.nominationId]);
    try {
      const token = await fetchToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/ApproveForUnitHead/${record.nominationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(response.data?.message || 'Approved on behalf of the unit head.');
      fetchNominations();
    } catch (error) {
      message.error(extractApiError(error, 'The nomination could not be approved on behalf of the unit head.'), 10);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== record.nominationId));
    }
  };

  // Admin rejects a pending GEM nomination at level 1 on behalf of its unit head. The API sets
  // approval1_status to Rejected with an explanatory comment (plus the optional reason); the unit
  // head's budget is not touched.
  const handleRejectForUnitHead = async (record: Nomination, reason: string) => {
    if (processingIds.includes(record.nominationId)) return;
    setProcessingIds(prev => [...prev, record.nominationId]);
    try {
      const token = await fetchToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/RejectForUnitHead/${record.nominationId}`,
        { reason: reason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(response.data?.message || 'Rejected on behalf of the unit head.');
      fetchNominations();
    } catch (error) {
      message.error(extractApiError(error, 'The nomination could not be rejected on behalf of the unit head.'), 10);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== record.nominationId));
    }
  };

  const unitHeadBalanceOf = (record: Nomination): number | null =>
    record.unitHeadAwardsCount === null || record.unitHeadAwardsCount === undefined
      ? null
      : Math.round(((record.unitHeadAwardsCount || 0) - (record.unitHeadAwardsUsed || 0)) * 100) / 100;

  // One confirmation pop-up for every decision on a row - UH Approve / UH Reject (level 1, on behalf of
  // the unit head) and Approve / Reject (final admin decision) - with what will happen and the
  // nomination's details. Nothing is sent until OK; the OK button spins until the API answers.
  type DecisionKind = 'uhApprove' | 'uhReject' | 'approve' | 'reject';

  const confirmDecision = (record: Nomination, kind: DecisionKind, showUnitHead: boolean) => {
    const unitHead = record.unitHeadName || 'the unit head';
    const nominees = record.nomineeNames?.join(', ') || 'N/A';
    const award = [record.rewardCategory, record.rewardName].filter(Boolean).join(' – ');
    const nominatedOn = record.nominationDate ? dayjs(record.nominationDate).format('DD MMM YYYY') : '-';
    const balance = unitHeadBalanceOf(record);
    const round = (n: number) => Math.round(n * 100) / 100;
    let reason = '';

    const config: Record<DecisionKind, { title: string; intro: string; okText: string; danger: boolean }> = {
      uhApprove: {
        title: `Approve on behalf of ${unitHead}?`,
        intro: `The nomination is approved as the unit head (level 1). One award is taken from ${unitHead}'s budget, and the nomination then waits for the final admin approval.`,
        okText: 'Approve',
        danger: false,
      },
      uhReject: {
        title: `Reject on behalf of ${unitHead}?`,
        intro: `The nomination is rejected as the unit head (level 1). ${unitHead}'s budget is not changed.`,
        okText: 'Reject',
        danger: true,
      },
      approve: {
        title: 'Approve this award?',
        intro: 'This is the final admin approval. The points are allocated to the nominee(s) and the award amount is taken from the main budget.',
        okText: 'Approve',
        danger: false,
      },
      reject: {
        title: 'Reject this award?',
        intro: 'This is the final admin decision. The nominee(s) receive no points and the main budget is not changed.',
        okText: 'Reject',
        danger: true,
      },
    };
    const { title, intro, okText, danger } = config[kind];

    const items: { key: string; label: string; children: React.ReactNode }[] = [
      { key: 'nominees', label: 'Nominee(s)', children: <strong>{nominees}</strong> },
      { key: 'nominator', label: 'Nominator', children: record.nominatorName || '-' },
      { key: 'award', label: 'Award', children: award || '-' },
      { key: 'unit', label: 'Unit', children: record.unit || '-' },
      { key: 'date', label: 'Nominated on', children: nominatedOn },
    ];
    if (showUnitHead) {
      items.push({ key: 'uh', label: 'Unit head', children: `${unitHead} (${record.approvalStatus || 'Pending'})` });
    }
    if (kind === 'uhApprove') {
      items.push({
        key: 'uhBudget',
        label: 'Unit head budget',
        children: balance === null
          ? 'No unit budget'
          : <>Total {round(record.unitHeadAwardsCount || 0)} · Used {round(record.unitHeadAwardsUsed || 0)} → <strong>{round((record.unitHeadAwardsUsed || 0) + 1)}</strong>
              {' '}· Balance {balance} → <strong>{round(balance - 1)}</strong></>,
      });
    }
    if (kind === 'approve') {
      items.push(
        { key: 'amount', label: 'Award amount', children: formatUSD(record.budget) },
        { key: 'main', label: 'Main budget', children: <>{formatUSD(budgetRemaining)} → <strong>{formatUSD(budgetRemaining - record.budget)}</strong></> },
      );
    }
    if (kind === 'reject') {
      items.push({ key: 'amount', label: 'Award amount', children: `${formatUSD(record.budget)} (not used)` });
    }

    Modal.confirm({
      title,
      width: 560,
      content: (
        <div>
          <p style={{ marginBottom: 12 }}>{intro}</p>
          <Descriptions size="small" column={1} bordered items={items} labelStyle={{ width: 150 }} />
          {kind === 'uhReject' && (
            <Input.TextArea
              rows={3}
              maxLength={500}
              style={{ marginTop: 12 }}
              placeholder="Reason (optional) - added to the approval comment"
              onChange={e => { reason = e.target.value; }}
            />
          )}
        </div>
      ),
      okText,
      okType: danger ? 'danger' : 'primary',
      okButtonProps: danger ? { danger: true } : undefined,
      cancelText: 'Cancel',
      onOk: () => {
        switch (kind) {
          case 'uhApprove': return handleApproveForUnitHead(record);
          case 'uhReject': return handleRejectForUnitHead(record, reason);
          case 'approve': return handleSubmitNomination(record.nominationId, 'Approved');
          case 'reject': return handleSubmitNomination(record.nominationId, 'Rejected');
        }
      },
    });
  };

  const adminColumns = [
    {
      title: t('No.'),
      dataIndex: 'approvalId',
      key: 'approvalId',
      render: (_: any, record: Nomination) => `${record.approvalId}`,
    },
    {
      title: t('reward_details'),
      dataIndex: 'rewardDetails',
      key: 'rewardDetails',
      render: (_: any, record: Nomination) => ` ${record.rewardName}`,
    },
    {
      title: t('nominee'),
      dataIndex: 'nomineeNames',
      key: 'nomineeNames',
      render: (nomineeNames: string[]) => (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100, display: 'inline-block' }}>
          {nomineeNames?.join(', ') || 'N/A'}
        </span>
      ),
    },
    {
      title: t('nominator'),
      dataIndex: 'nominatorName',
      key: 'nominatorName',
    },
    {
      title: t('AdminActions'),
      key: 'actions',
      render: (_: any, record: Nomination) => {
        const noBudget = budgetRemaining < record.budget;
        const alreadyActioned = record.adminApproval === "Approved" || record.adminApproval=="Rejected";
        const isProcessing = processingIds.includes(record.nominationId);
        const approveDisabled = alreadyActioned  || noBudget || isProcessing;
        const rejectDisabled = alreadyActioned  || noBudget || isProcessing;
        const deleteDisabled=alreadyActioned || isProcessing;
        // const approveDisabled = false;  
        // const rejectDisabled = false;
        const confirmDelete = () => {
          Modal.confirm({
            title: 'Are you sure you want to delete this nomination? It will be permanently removed.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => handleDeleteNomination(record.nominationId),
          });
        };

        return (
          <div className="aa-actions" style={{ flexWrap: 'wrap' }}>
            {record.adminApproval === 'Approved' ? (
                <Button type="primary" disabled style={{ backgroundColor: 'green', borderColor: 'white', color: 'white' }}>
                  Approved
                </Button>
              ) : (
                <Tooltip
                  title={
                    noBudget
                      ? 'No budget'
                      : alreadyActioned
                        ? 'Action not allowed'
                        : Math.floor(budgetUsed) >= Math.floor(totalBudget)
                          ? 'No budget to approve'
                          : ''
                  }
                >
                  <Button
                    icon={<CheckOutlined />}
                    type="primary"
                    disabled={approveDisabled}
                    onClick={() => confirmDecision(record, 'approve', false)}
                  >
                    Approve
                  </Button>
                </Tooltip>
              )}
              {record.adminApproval === 'Rejected' ? (
                <Button type="primary" disabled style={{ backgroundColor: 'Red', borderColor: 'Red', color: 'White' }}>
                  Rejected
                </Button>
              ) : (
                <Tooltip title={noBudget ? 'No budget' : ''}>
                  <Button
                    icon={<CloseOutlined />}
                    danger
                    disabled={rejectDisabled}
                    onClick={() => confirmDecision(record, 'reject', false)}
                  >
                    Reject
                  </Button>
                </Tooltip>
              )}
            <span className="aa-actions__divider" />
            <IconAction title="View nomination" variant="view" icon={<EyeOutlined />}
              onClick={() => handleViewNomination(record.nominationId)} />
            <IconAction title={deleteDisabled ? "Can't delete an actioned nomination" : 'Delete nomination'} variant="delete"
              icon={<DeleteOutlined />} onClick={confirmDelete} disabled={deleteDisabled} />
          </div>
        );
      },
    },
  ];
  const columns = [
    {
      title: t('No.'),
      dataIndex: 'approvalId',
      key: 'approvalId',
      render: (_: any, record: Nomination) => `${record.approvalId}`,

    },
    {
      title: t('nominee'),
      dataIndex: 'nomineeNames',
      key: 'nomineeNames',
      render: (nomineeNames: string[]) => (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100, display: 'inline-block' }}>
          {nomineeNames?.join(', ') || 'N/A'}
        </span>
      ),
    },
    {
      title: t('nominator'),
      dataIndex: 'nominatorName',
      key: 'nominatorName',
    },
    {
      title: t('UH_Status'),
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      render: (approvalStatus: string) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={approvalStatus === 'Approved' ? 'green' : approvalStatus === 'Rejected' ? 'red' : 'blue'}>
            {approvalStatus}
          </Tag>
        </div>
      ),
    },
    {
      title: t('AdminActions'),
      key: 'actions',
      render: (_: any, record: Nomination) => {
        const noBudget = budgetRemaining < record.budget;
        const alreadyActioned = record.adminApproval !== null;
        const pendingStatus = record.approvalStatus === 'Pending';
        const isProcessing = processingIds.includes(record.nominationId);
        const approveDisabled = alreadyActioned || pendingStatus || noBudget || record.approvalStatus === 'Rejected' || isProcessing;
        const rejectDisabled = alreadyActioned || pendingStatus || noBudget || record.approvalStatus === 'Rejected' || isProcessing;
        const deleteDisabled=alreadyActioned || isProcessing;

        // UH Approve: the admin approves at level 1 on behalf of the unit head (approval1_user_id).
        // Only while the unit head has not acted, and only with a whole award left in their budget -
        // the same rule the API enforces.
        const unitHead = record.unitHeadName || 'the unit head';
        const unitHeadBalance = unitHeadBalanceOf(record);
        const unitHeadHasAward = unitHeadBalance !== null && unitHeadBalance >= 1;
        const unitHeadBudgetText = unitHeadBalance === null
          ? `${unitHead} has no unit budget`
          : `${unitHead}: Total ${record.unitHeadAwardsCount}, Used ${record.unitHeadAwardsUsed}, Balance ${unitHeadBalance}`;
        const uhApproveDisabled = !pendingStatus || !unitHeadHasAward || isProcessing;
        const uhApproveTooltip = !pendingStatus
          ? `The unit head has already ${record.approvalStatus?.toLowerCase() || 'actioned'} this nomination`
          : unitHeadHasAward
            ? `Approve on behalf of ${unitHeadBudgetText}`
            : `Can't approve - ${unitHeadBudgetText}`;

        return (
          <div className="aa-actions">
            <Tooltip title={uhApproveTooltip}>
              <Button
                icon={<CheckOutlined />}
                disabled={uhApproveDisabled}
                loading={isProcessing && pendingStatus}
                onClick={() => confirmDecision(record, 'uhApprove', true)}
              >
                UH Approve
              </Button>
            </Tooltip>
            <Tooltip title={pendingStatus
              ? `Reject on behalf of ${unitHead}`
              : `The unit head has already ${record.approvalStatus?.toLowerCase() || 'actioned'} this nomination`}>
              <Button
                icon={<CloseOutlined />}
                danger
                disabled={!pendingStatus || isProcessing}
                onClick={() => confirmDecision(record, 'uhReject', true)}
              >
                UH Reject
              </Button>
            </Tooltip>
            {record.adminApproval === 'Approved' ? (
              <Button type="primary" disabled style={{ backgroundColor: 'green', borderColor: 'white', color: 'white' }}>
                Approved
              </Button>
            ) : (
              <Tooltip
                title={
                  noBudget
                    ? 'No budget'
                    : alreadyActioned
                      ? 'Action not allowed'
                      : Math.floor(budgetUsed) >= Math.floor(totalBudget)
                        ? 'No budget to approve'
                        : ''
                }
              >
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  disabled={approveDisabled}
                  onClick={() => confirmDecision(record, 'approve', true)}
                >
                  Approve
                </Button>
              </Tooltip>
            )}
            {record.adminApproval === 'Rejected' ? (
              <Button type="primary" disabled style={{ backgroundColor: 'Red', borderColor: 'Red', color: 'White' }}>
                Rejected
              </Button>
            ) : (
              <Tooltip title={noBudget ? 'No budget' : ''}>
                <Button
                  icon={<CloseOutlined />}
                  danger
                  disabled={rejectDisabled}
                  onClick={() => confirmDecision(record, 'reject', true)}
                >
                  Reject
                </Button>
              </Tooltip>
            )}
            <span className="aa-actions__divider" />
            <IconAction title="View nomination" variant="view" icon={<EyeOutlined />}
              onClick={() => handleViewNomination(record.nominationId)} />
            <IconAction title={deleteDisabled ? "Can't delete an actioned nomination" : 'Delete nomination'} variant="delete"
              icon={<DeleteOutlined />} onClick={() => handleDeleteNomination(record.nominationId)} disabled={deleteDisabled} />
          </div>
        );
      },
    },
  ];

  // GEM Approvals rows after applying the Year/Quarter/Month filter
  // and the search box. Each sub-tab (All/Pending/Approved/Rejected) applies its
  // own adminApproval filter on top of this.
  const filteredRewardsNominations = nominations
    .filter(n => matchesDateFilter(n.nominationDate))
    .filter(n =>
      n.nomineeNames.join(', ').toLowerCase().includes(rewardsApprovalSearch.toLowerCase()) ||
      n.nominatorName.toLowerCase().includes(rewardsApprovalSearch.toLowerCase()) ||
      n.rewardName.toLowerCase().includes(rewardsApprovalSearch.toLowerCase())
    );

  const pagingWithSize = (pageSize: number, setPageSize: (size: number) => void) => ({
    pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total}`,
    onChange: (_page: number, size: number) => setPageSize(size),
  });
  const gemPagination = pagingWithSize(gemPageSize, setGemPageSize);
  const adminPagination = pagingWithSize(adminPageSize, setAdminPageSize);

  // ----- Admin_Only_Approvals tab filters -----
  // Years available in the admin data (plus current year), newest first.
  const adminAvailableYears = (() => {
    const years = new Set<number>([currentYear]);
    adminNominations.forEach(n => {
      if (n.nominationDate) {
        const y = new Date(n.nominationDate).getFullYear();
        if (!isNaN(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  })();

  // Unique award names taken from the Reward Details column, alphabetically.
  const adminAwardNames = Array.from(
    new Set(adminNominations.map(n => n.rewardName).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const matchesAdminYear = (dateStr: string): boolean => {
    if (adminSelectedYear === 'All') return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    return d.getFullYear() === Number(adminSelectedYear);
  };

  // Admin_Only_Approvals rows after applying the Year + Award Name filters and the
  // search box. Each sub-tab (All/Pending/Approved/Rejected) applies its own
  // adminApproval filter on top of this.
  const filteredAdminNominations = adminNominations
    .filter(n => matchesAdminYear(n.nominationDate))
    .filter(n => adminSelectedAward === 'All' || n.rewardName === adminSelectedAward)
    .filter(n =>
      n.nomineeNames.join(', ').toLowerCase().includes(adminOnlyApprovalSearch.toLowerCase()) ||
      n.nominatorName.toLowerCase().includes(adminOnlyApprovalSearch.toLowerCase()) ||
      n.rewardName.toLowerCase().includes(adminOnlyApprovalSearch.toLowerCase())
    );

  // ----- Admin_Only_Approvals bulk approve/reject -----
  const isAdminActioned = (n: Nomination) => n.adminApproval === 'Approved' || n.adminApproval === 'Rejected';

  // Only rows that are visible under the current filters and still pending count as selected,
  // so a filter/search change can never approve rows the admin can no longer see.
  const selectedAdminNominations = filteredAdminNominations.filter(
    n => adminSelectedKeys.includes(n.nominationId) && !isAdminActioned(n)
  );
  const selectedAdminBudget = selectedAdminNominations.reduce((sum, n) => sum + (Number(n.budget) || 0), 0);

  useEffect(() => {
    setAdminSelectedKeys([]);
  }, [adminSelectedYear, adminSelectedAward, adminOnlyApprovalSearch]);

  const adminRowSelection = {
    selectedRowKeys: adminSelectedKeys,
    onChange: (keys: React.Key[], _rows: Nomination[], info?: { type: string }) => {
      // The header checkbox selects every pending row matching the filters (all pages),
      // not just the current page; clicking it again clears the selection.
      if (info?.type === 'all') {
        const selecting = keys.some(k => !adminSelectedKeys.includes(k));
        setAdminSelectedKeys(selecting
          ? filteredAdminNominations
              .filter(n => !isAdminActioned(n) && !processingIds.includes(n.nominationId))
              .map(n => n.nominationId)
          : []);
        return;
      }
      setAdminSelectedKeys(keys);
    },
    getCheckboxProps: (record: Nomination) => ({
      disabled: isAdminActioned(record) || processingIds.includes(record.nominationId) || bulkProcessing,
    }),
  };

  // Submit each selected nomination on its own request, one after another. The API stops at the
  // first failure in a batch without saying which ones went through; per-nomination calls let us
  // report exactly what succeeded and what failed (and why).
  const runBulkAction = async (action: 'Approved' | 'Rejected') => {
    const userid = UserDetails[0]?.id;
    const targets = selectedAdminNominations;
    if (!userid || targets.length === 0) return;

    setBulkProcessing(true);
    setProcessingIds(prev => [...prev, ...targets.map(n => n.nominationId)]);
    const failures: string[] = [];
    let succeeded = 0;
    const hide = message.loading(`${action === 'Approved' ? 'Approving' : 'Rejecting'} ${targets.length} nomination(s)...`, 0);

    try {
      for (const n of targets) {
        try {
          const token = await fetchToken();
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/UploadApprovalsData`,
            { UserId: userid, NominationLists: [{ NominationId: n.nominationId, Status: action }] },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          succeeded++;
        } catch (error) {
          failures.push(`${n.nomineeNames?.join(', ') || n.nominationId} (${n.rewardName}): ${extractApiError(error, 'failed')}`);
          // Re-enable only the failed rows so they can be retried.
          setProcessingIds(prev => prev.filter(id => id !== n.nominationId));
        }
      }
    } finally {
      hide();
      setBulkProcessing(false);
      setAdminSelectedKeys([]);
      fetchAdminNominations();
      fetchNominations();
      fetchAwardsData();
    }

    const verb = action === 'Approved' ? 'approved' : 'rejected';
    if (failures.length === 0) {
      message.success(`${succeeded} nomination(s) ${verb}.`);
    } else {
      Modal.warning({
        title: `${succeeded} ${verb}, ${failures.length} failed`,
        width: 600,
        content: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {failures.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        ),
      });
    }
  };

  const confirmBulkApprove = () => {
    const count = selectedAdminNominations.length;
    if (selectedAdminBudget > budgetRemaining) {
      message.error(`Selected nominations need ${formatUSD(selectedAdminBudget)} but only ${formatUSD(budgetRemaining)} is left in the budget.`);
      return;
    }
    Modal.confirm({
      title: `Approve ${count} selected nomination(s)?`,
      content: `Total budget: ${formatUSD(selectedAdminBudget)}`,
      okText: 'Approve All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => { runBulkAction('Approved'); },
    });
  };

  const confirmBulkReject = () => {
    const count = selectedAdminNominations.length;
    Modal.confirm({
      title: `Reject ${count} selected nomination(s)?`,
      okText: 'Reject All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => { runBulkAction('Rejected'); },
    });
  };

  const adminBulkBar = (
    <Space wrap style={{ marginBottom: 12 }}>
      <span>
        {selectedAdminNominations.length > 0
          ? `${selectedAdminNominations.length} selected · ${formatUSD(selectedAdminBudget)}`
          : 'Select nominations using the checkboxes to approve or reject them together'}
      </span>
      <Button
        type="primary"
        icon={<CheckOutlined />}
        disabled={selectedAdminNominations.length === 0 || bulkProcessing}
        loading={bulkProcessing}
        onClick={confirmBulkApprove}
      >
        Approve Selected
      </Button>
      <Button
        danger
        icon={<CloseOutlined />}
        disabled={selectedAdminNominations.length === 0 || bulkProcessing}
        onClick={confirmBulkReject}
      >
        Reject Selected
      </Button>
      {selectedAdminNominations.length > 0 && !bulkProcessing && (
        <Button type="link" onClick={() => setAdminSelectedKeys([])}>Clear selection</Button>
      )}
    </Space>
  );

  return (
    <div style={{ padding: '10px 0', backgroundColor: 'white' }}>
      <div style={{ marginBottom: '20px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '10px',
            boxShadow: '0 1px 1px rgba(215, 219, 219, 0.9)',
            borderRadius: '8px',
            overflow: 'hidden',
            margin: 0,
            padding: 0,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', textAlign: 'center' }}>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',

                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('Total_Budget')}
              </th>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('Used_Budget')}
              </th>
              <th
                style={{
                  width: '33%',
                  padding: '12px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                {t('balance')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{formatUSD(totalBudget)}</strong>
              </td>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{formatUSD(budgetUsed)}</strong>
              </td>
              <td
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                  fontSize: '14px',
                  color: '#262626',
                }}
              >
                <strong>{formatUSD(budgetRemaining)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Tabs defaultActiveKey="1" activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: 32 }}>
        {/* GEM Approvals */}
        <Tabs.TabPane tab={t('GEM_Approvals')} key="1">
          <div style={{ marginBottom: 16 }}>
            <Space wrap style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="Search by nominee, nominator, or reward"
                value={rewardsApprovalSearch}
                onChange={e => setRewardsApprovalSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 120 }}
                options={[
                  { value: 'All', label: t('All') },
                  ...availableYears.map(y => ({ value: String(y), label: String(y) })),
                ]}
              />
              <Select
                value={selectedQuarter}
                onChange={handleQuarterChange}
                style={{ width: 150 }}
                options={[
                  { value: 'All', label: t('All') },
                  { value: '1', label: 'Q1 (Jan-Mar)' },
                  { value: '2', label: 'Q2 (Apr-Jun)' },
                  { value: '3', label: 'Q3 (Jul-Sep)' },
                  { value: '4', label: 'Q4 (Oct-Dec)' },
                ]}
              />
              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                style={{ width: 150 }}
                options={[
                  { value: 'All', label: t('All') },
                  ...monthOptions.map(m => ({ value: String(m), label: MONTH_NAMES[m - 1] })),
                ]}
              />
            </Space>
          </div>
          <Tabs defaultActiveKey="pending" style={{ marginBottom: 16 }}>
            <Tabs.TabPane tab={t('All')} key="All">
              <Table<Nomination>
                dataSource={filteredRewardsNominations}
                columns={columns}
                loading={rewardsLoading}
                pagination={gemPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('Pending')} key="pending">
              <Table<Nomination>
                dataSource={filteredRewardsNominations.filter(n => n.adminApproval == null)}
                columns={columns}
                loading={rewardsLoading}
                pagination={gemPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab={t('Approved')} key="approved">
              <Table<Nomination>
                dataSource={filteredRewardsNominations.filter(n => n.adminApproval === 'Approved')}
                columns={columns}
                loading={rewardsLoading}
                pagination={gemPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('Rejected')} key="Rejected">
              <Table<Nomination>
                dataSource={filteredRewardsNominations.filter(n => n.adminApproval === 'Rejected')}
                columns={columns}
                loading={rewardsLoading}
                pagination={gemPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
          </Tabs>
        </Tabs.TabPane>
        {/* Admin Only Approvals */}
        <Tabs.TabPane tab={t('Admin_Only_Approvals')} key="2">
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              placeholder="Search by nominee, nominator, or reward"
              value={adminOnlyApprovalSearch}
              onChange={e => setAdminOnlyApprovalSearch(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              value={adminSelectedYear}
              onChange={setAdminSelectedYear}
              style={{ width: 120 }}
              options={[
                { value: 'All', label: t('All') },
                ...adminAvailableYears.map(y => ({ value: String(y), label: String(y) })),
              ]}
            />
            <Select
              value={adminSelectedAward}
              onChange={setAdminSelectedAward}
              style={{ width: 200 }}
              showSearch
              placeholder={t('reward_details')}
              options={[
                { value: 'All', label: t('All') },
                ...adminAwardNames.map(name => ({ value: name, label: name })),
              ]}
            />
          </Space>
          <Tabs defaultActiveKey="pending" style={{ marginBottom: 16 }}>
            <Tabs.TabPane tab={t('All')} key="All">
              {adminBulkBar}
              <Table<Nomination>
                dataSource={filteredAdminNominations}
                columns={adminColumns}
                loading={adminLoading}
                pagination={adminPagination}
                rowSelection={adminRowSelection}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('Pending')} key="pending">
              {adminBulkBar}
              <Table<Nomination>
                dataSource={filteredAdminNominations.filter(n => n.adminApproval === '')}
                columns={adminColumns}
                loading={adminLoading}
                pagination={adminPagination}
                rowSelection={adminRowSelection}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('Approved')} key="approved">
              <Table<Nomination>
                dataSource={filteredAdminNominations.filter(n => n.adminApproval === 'Approved')}
                columns={adminColumns}
                loading={adminLoading}
                pagination={adminPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('Rejected')} key="Rejected">
              <Table<Nomination>
                dataSource={filteredAdminNominations.filter(n => n.adminApproval === 'Rejected')}
                columns={adminColumns}
                loading={adminLoading}
                pagination={adminPagination}
                rowKey="nominationId"
                style={{ overflowX: 'auto' }}
                className="custom-table"
              />
            </Tabs.TabPane>
          </Tabs>
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('All_Nominations')} key="3">
          <Input.Search
            placeholder="Search by nominee, nominator, or reward"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ marginBottom: 16, width: 300 }}
            allowClear
          />
          <Table<Nomination>
            loading={allLoading}
            dataSource={allNominations.filter(n =>
              n.nomineeNames.join(', ').toLowerCase().includes(searchText.toLowerCase()) ||
              n.nominatorName.toLowerCase().includes(searchText.toLowerCase()) ||
              n.rewardName.toLowerCase().includes(searchText.toLowerCase())
            )}
            columns={[
              {
                title: t('No.'),
                dataIndex: 'approvalId',
                render: (_: any, record: Nomination) => `${record.approvalId}`,
              },
              {
                title: t('reward_details'),
                dataIndex: 'rewardDetails',
                key: 'rewardDetails',
                render: (_: any, record: Nomination) => ` ${record.rewardName}`,
              },
              {
                title: t('nominee'),
                dataIndex: 'nomineeNames',
                key: 'nomineeNames',
                render: (nomineeNames: string[]) => (
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100, display: 'inline-block' }}>
                    {nomineeNames?.join(', ') || 'N/A'}
                  </span>
                ),
              },
              {
                title: t('nominator'),
                dataIndex: 'nominatorName',
                key: 'nominatorName',
              },
              {
                title: t('Actions'),
                key: 'actions',
                render: (_: any, record: Nomination) => (
                  <div className="aa-actions">
                    <IconAction title="View nomination" variant="view" icon={<EyeOutlined />}
                      onClick={() => handleViewNomination(record.nominationId)} />
                    <IconAction title="Delete nomination" variant="delete" icon={<DeleteOutlined />}
                      onClick={() => handleDeleteNomination(record.nominationId)} />
                  </div>
                ),
              },
            ]}
            rowKey="nominationId"
            style={{ overflowX: 'auto' }}
            className="custom-table"
          />
        </Tabs.TabPane>

      </Tabs>
      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={700}
        style={{ padding: '10px' }}
      >
        {selectedNomination && (
          <div
            style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: '16px',
                color: 'rgba(0, 0, 0, 0.88)',
              }}
            >
              {t('reward_details')}
            </h3>
            <Card bordered={false} style={{ marginTop: '16px' }}>
              <Row gutter={[16, 16]} style={{ rowGap: '8px' }}>
                <Col span={12}>
                  <strong>{t('rewards')}</strong>
                  <br /> {selectedNomination.rewardCategory} - {selectedNomination.rewardName}
                </Col>
                <Col span={12}>
                  <strong>{t('nomination_date')}</strong>
                  <br /> {new Date(selectedNomination.nominationDate).toLocaleDateString()}
                </Col>
                <Col span={12}>
                  <strong>{t('month_year')}</strong>
                  <br /> {selectedNomination.nominationMonth} {selectedNomination.nominationYear}
                </Col>
                <Col span={12}>
                  <strong>{t('nominated_by')}</strong>
                  <br /> {selectedNomination.nominatorName || 'N/A'}
                </Col>
                <Col span={12}>
                  <strong>{t('awarded_to')}</strong>
                  <br /> {selectedNomination.nomineeNames?.join(', ') || 'N/A'}
                </Col>
                <Col span={12}>
                  <strong>{t('awarded_points')}</strong>
                  <br /> {selectedNomination.budget} points
                </Col>
                {selectedNomination.approverLevel1 && selectedNomination.approverLevel2 &&
                  <>
                    <Col span={12}>
                      <strong>{t('approval_level1')}</strong>
                      <br /> {selectedNomination.approverLevel1.charAt(0).toUpperCase() + selectedNomination.approverLevel1.slice(1)}
                    </Col>
                    <Col span={12}>
                      <strong>{t('approval_level2')}</strong>
                      <br /> {selectedNomination.approverLevel2.charAt(0).toUpperCase() + selectedNomination.approverLevel2.slice(1)}
                    </Col>
                  </>
                }
                <Col span={12}>
                  <strong>{t('value_demonstrated')}</strong>
                  <br />
                  <Tag
                    style={{
                      backgroundColor: '#8629A4',
                      color: 'white',
                      padding: '4px',
                    }}
                  >
                    {selectedNomination.valueDemonstrated}
                  </Tag>
                </Col>
                {selectedNomination.ecardImage && (
                  <Col span={24}>
                    <div>
                      <strong>{t('ecard')}</strong>
                    </div>
                    <div>
                      <img
                        src={`data:image/jpeg;base64,${selectedNomination.ecardImage}`}
                        alt="E-Card"
                        style={{
                          width: '100%',
                          maxWidth: '300px',
                          height: 'auto',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          marginTop: '10px',
                        }}
                      />
                    </div>
                  </Col>
                )}
                {/* <Col span={24}>
                  <strong>{t('reward_testimonial')}</strong>
                  <br />
                  <Input.TextArea
                    value={extractCitation(selectedNomination.rewardTestimonial)}
                    readOnly
                    style={{
                      width: '100%',
                      height: '80px',
                      resize: 'none',
                    }}
                  />
                </Col> */}
                <Col span={24}>
                  <strong>{t('reward_testimonial')}</strong>
                  <br />
                  {isEditingCitation ? (
                    <>
                      <strong>Citation</strong>
                      <Input.TextArea
                        value={editedCitation}
                        onChange={e => setEditedCitation(e.target.value)}
                        style={{ width: '100%', height: '60px', resize: 'none', marginBottom: 8 }}
                      />
                      <strong>Contribution Towards</strong>
                      <Input.TextArea
                        value={editedContribution}
                        onChange={e => setEditedContribution(e.target.value)}
                        style={{ width: '100%', height: '60px', resize: 'none', marginBottom: 8 }}
                      />
                      <Button
                        type="primary"
                        style={{ marginTop: 8, marginRight: 8 }}
                        onClick={async () => {
                          if (!selectedNomination) return;
                          try {
                            const token = await fetchToken();
                            // Extract the ~FormFields~ section if present
                            const formFieldsMatch = selectedNomination.rewardTestimonial.match(/(~FormFields~[\s\S]*)/);
                            let formFields = formFieldsMatch ? formFieldsMatch[0] : '';

                            // Update only the contributionTowards part in formFields
                            if (formFields) {
                              formFields = formFields.replace(
                                /(~businessOutcome~\s*)([\s\S]*?)(\)?)/,
                                `$1$2\n${editedContribution}$3`
                              );
                            }

                            // Construct the new citation string in the same format
                            const updatedCitation =
                              '~citation~\n' + editedCitation + (formFields ? '\n' + formFields : '');

                            await axios.put(
                              `${process.env.REACT_APP_API_URL}/api/AdminUploadApproval/edit-citation/${selectedNomination.nominationId}`,
                              JSON.stringify(updatedCitation),
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                              }
                            );
                            setIsEditingCitation(false);
                            handleViewNomination(selectedNomination.nominationId);
                          } catch (error) {
                            console.error('Error updating citation', error);
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          setIsEditingCitation(false);
                          setEditedCitation(extractCitation(selectedNomination.rewardTestimonial));
                          setEditedContribution(extractBusinessOutcome(selectedNomination.rewardTestimonial));
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Input.TextArea
                        value={extractCitation(selectedNomination.rewardTestimonial)}
                        readOnly
                        style={{ width: '100%', height: '80px', resize: 'none', marginBottom: 8 }}
                      />
                      {extractBusinessOutcome(selectedNomination.rewardTestimonial) && (
                        <>
                          <strong>Business Outcome</strong>
                          <Input.TextArea
                            value={extractBusinessOutcome(selectedNomination.rewardTestimonial)}
                            readOnly
                            style={{ width: '100%', height: '60px', resize: 'none', marginBottom: 8 }}
                          />
                        </>
                      )}
                      <Button
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          setIsEditingCitation(true);
                          setEditedCitation(extractCitation(selectedNomination.rewardTestimonial));
                          setEditedContribution(extractBusinessOutcome(selectedNomination.rewardTestimonial));
                        }}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminApprovalComponent;